"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Edit,
  Trash,
  X,
  Check,
  Upload,
  MapPin,
  DollarSign,
  BarChart3,
  Clock,
  Target,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://backend.emerald.azadnoori.com";

export default function PlansManagementTab() {
  const { token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState("");
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);

  // Add a toast message
  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  };

  // Remove a toast manually
  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/plans`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mappedPlans = response.data.data.Plan.map((plan) => ({
          ...plan,
          returnRate: plan.return_rate,
        }));
        setPlans(mappedPlans);
        setError(null);
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to fetch plans";
        setError(errorMessage);
        addToast(errorMessage, "error");
      }
    };

    fetchPlans();

    const handlePlanAdded = () => fetchPlans();
    window.addEventListener("storage", handlePlanAdded);
    return () => window.removeEventListener("storage", handlePlanAdded);
  }, [token]);

  const filteredPlans = plans.filter(
    (plan) =>
      plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.focus.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditPlan = (plan) => {
    setEditingPlan({ ...plan, image: null });
    setCoverImagePreview(plan.image);
    setShowEditModal(true);
  };

  const handleDeletePlan = (planId) => {
    setPlanToDelete(planId);
    setShowDeleteConfirm(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingPlan({ ...editingPlan, [name]: value });
  };

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith("image/")) {
        setError("Please upload an image file");
        addToast("Please upload an image file", "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        addToast("Image size must be less than 5MB", "error");
        return;
      }
      const imageUrl = URL.createObjectURL(file);
      setCoverImagePreview(imageUrl);
      setEditingPlan({ ...editingPlan, image: file });
    }
  };

  const savePlanChanges = async () => {
    const requiredFields = [
      "name",
      "description",
      "price",
      "returnRate",
      "duration",
      "focus",
      "performance",
      "location",
    ];
    for (const field of requiredFields) {
      if (!editingPlan[field] || editingPlan[field] === "") {
        const errorMessage = `Please fill in the ${field} field`;
        setError(errorMessage);
        addToast(errorMessage, "error");
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("name", editingPlan.name);
      formData.append("description", editingPlan.description);
      formData.append("price", editingPlan.price);
      formData.append("return_rate", editingPlan.returnRate);
      formData.append("duration", editingPlan.duration);
      formData.append("focus", editingPlan.focus);
      formData.append("performance", editingPlan.performance);
      formData.append("location", editingPlan.location);
      if (editingPlan.image instanceof File) {
        formData.append("image", editingPlan.image);
      }

      const response = await axios.put(`${API_BASE_URL}/api/v1/plans/${editingPlan.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setPlans(
        plans.map((plan) =>
          plan.id === editingPlan.id
            ? { ...response.data.data.updatedPlan, returnRate: response.data.data.updatedPlan.return_rate }
            : plan
        )
      );
      setShowEditModal(false);
      setError(null);
      addToast("Plan updated successfully", "success");
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update plan";
      setError(errorMessage);
      addToast(errorMessage, "error");
    }
  };

  const confirmDeletePlan = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/plans/${planToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlans(plans.filter((plan) => plan.id !== planToDelete));
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
      setError(null);
      addToast("Plan deleted successfully", "success");
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to delete plan";
      setError(errorMessage);
      addToast(errorMessage, "error");
    }
  };

  return (
    <div className="relative">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className={`flex items-center justify-between p-4 rounded-lg shadow-lg ${
                toast.type === "success"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : "bg-red-100 text-red-800 border-red-200"
              } border`}
            >
              <div className="flex items-center">
                {toast.type === "success" ? (
                  <Check className="mr-2" size={20} />
                ) : (
                  <X className="mr-2" size={20} />
                )}
                <span>{toast.message}</span>
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="ml-4 text-gray-500 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Existing Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-100 border border-red-200 text-red-800 rounded-lg p-4 mb-6"
        >
          {error}
        </motion.div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search plans..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center text-sm text-gray-500 ml-4">
          <BarChart3 className="mr-2" size={16} />
          <span>Total Plans: {plans.length}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlans.length > 0 ? (
          filteredPlans.map((plan) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="h-48 bg-gray-200 relative">
                <img
                  src={plan.image || "/placeholder.svg"}
                  alt={plan.name}
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                />
                <div className="absolute top-4 right-4 bg-emerald-600 text-white px-2 py-1 rounded-md text-sm font-medium">
                  {plan.returnRate}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{plan.description}</p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin size={14} className="mr-1 flex-shrink-0" />
                    <span className="truncate">{plan.location}</span>
                  </div>
                  <div className="flex items-center text-sm font-medium text-emerald-600">
                    <DollarSign size={14} className="mr-1 flex-shrink-0" />${plan.price}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock size={14} className="mr-1 flex-shrink-0" />
                    <span className="truncate">{plan.duration}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Target size={14} className="mr-1 flex-shrink-0" />
                    <span className="truncate">{plan.focus}</span>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleEditPlan(plan)}
                    className="p-1.5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                    title="Edit Plan"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDeletePlan(plan.id)}
                    className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                    title="Delete Plan"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-gray-500">No plans found matching your search.</div>
        )}
      </div>

      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Edit Plan</h3>
                <button onClick={() => setShowEditModal(false)} className="p-1 rounded-full hover:bg-gray-100">
                  <X size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={editingPlan?.name || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <input
                        id="location"
                        name="location"
                        type="text"
                        value={editingPlan?.location || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                        Price ($)
                      </label>
                      <input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        value={editingPlan?.price || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="returnRate" className="block text-sm font-medium text-gray-700 mb-1">
                        Return Rate (e.g. "12%")
                      </label>
                      <input
                        id="returnRate"
                        name="returnRate"
                        type="text"
                        value={editingPlan?.returnRate || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (e.g. "12 months")
                      </label>
                      <input
                        id="duration"
                        name="duration"
                        type="text"
                        value={editingPlan?.duration || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="focus" className="block text-sm font-medium text-gray-700 mb-1">
                        Focus (e.g. "Low Risk", "Growth")
                      </label>
                      <input
                        id="focus"
                        name="focus"
                        type="text"
                        value={editingPlan?.focus || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={3}
                        value={editingPlan?.description || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="performance" className="block text-sm font-medium text-gray-700 mb-1">
                        Performance (e.g. "+12% last quarter")
                      </label>
                      <input
                        id="performance"
                        name="performance"
                        type="text"
                        value={editingPlan?.performance || ""}
                        onChange={handleEditInputChange}
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center mb-4">
                        <input
                          type="file"
                          id="coverImage"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="coverImage"
                          className="flex flex-col items-center justify-center cursor-pointer"
                        >
                          <Upload className="h-10 w-10 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Click to upload new image</span>
                          <span className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</span>
                        </label>
                      </div>

                      <div className="bg-gray-100 rounded-lg overflow-hidden h-32 mb-4">
                        {coverImagePreview ? (
                          <img
                            src={coverImagePreview || "/placeholder.svg"}
                            alt="Cover Preview"
                            className="w-full h-full object-cover"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-400">No image selected</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={savePlanChanges}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
                >
                  <Check size={18} className="mr-2" />
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this plan? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletePlan}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                >
                  <Trash size={18} className="mr-2" />
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}